import React, { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ImageService from '../images/imageService';
import ContentService from './ContentService';
import { marked } from 'marked';
import './ContentHandler.css';

const ContentHandler = () => {
  const { id } = useParams(); // if present => edit mode
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [contentData, setContentData] = useState({
    title: '',
    category: '',
    extraData: '',
    fontSize: '18',
    content: '',
    isMarkdown: false,
    images: '',
  });

  const [gallery, setGallery] = useState([]);
  const [slug, setSlug] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load content when editing
  useEffect(() => {
    if (!isEditMode) return;

    const loadContent = async () => {
      try {
        const data = await ContentService.getContent(id);

        const fontSize =
          (data.font_size && String(data.font_size)) ||
          (data.fontSize && String(data.fontSize)) ||
          '18';

        const images = data.images || '';
        const imageUrls = images
          ? images
              .split(',')
              .map((u) => u.trim())
              .filter(Boolean)
          : [];

        setContentData({
          title: data.title || '',
          category: data.category || '',
          extraData: data.extraData || '',
          fontSize,
          content: data.content || '',
          isMarkdown: false,
          images,
        });

        setGallery(imageUrls.map((url) => ({ url, selected: false })));

        const newSlug =
          data.slug ||
          (data.title || '')
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9\-]/g, '');
        setSlug(newSlug);
      } catch (err) {
        console.error('Failed to load content for editing', err);
        alert('Failed to load content.');
      }
    };

    loadContent();
  }, [id, isEditMode]);

  const handleInputChange = (key, value) => {
    setContentData((prev) => ({ ...prev, [key]: value }));
    if (key === 'title') {
      const newSlug = value
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9\-]/g, '');
      setSlug(newSlug);
    }
  };

  const applyFormat = (tag) => {
    document.execCommand(tag, false, null);
  };

  const applyHeading = (level) => {
    document.execCommand('formatBlock', false, `<h${level}>`);
  };

  const insertImageAtCursor = (url) => {
    const imgTag = `<img src="${url}" alt="" style="max-width:100%;" />`;
    document.execCommand('insertHTML', false, imgTag);
  };

  const handleImageUpload = async (files) => {
    const uploaded = [];
    for (let file of files) {
      const filename = `${slug || 'content'}_${file.name}`;
      const path = 'content';
      const imageUrl = await ImageService.uploadImage(file, filename, path);
      uploaded.push(imageUrl);
      insertImageAtCursor(imageUrl);
    }

    const combined = [...gallery.map((img) => img.url), ...uploaded];
    setGallery(combined.map((url) => ({ url, selected: false })));
    setContentData((prev) => ({ ...prev, images: combined.join(',') }));
  };

  const handleImageDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    await handleImageUpload(files);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    await handleImageUpload(files);
  };

  const handleImageSelection = (url) => {
    setGallery((prevGallery) =>
      prevGallery.map((img) =>
        img.url === url ? { ...img, selected: !img.selected } : img
      )
    );
  };

  const handleDeleteImages = async () => {
    const selected = gallery.filter((img) => img.selected);
    for (let img of selected) {
      const parts = img.url.split('/');
      const filename = parts.pop();
      const dir = parts.slice(-2, -1)[0];
      await ImageService.deleteImage(dir, filename);
    }
    const remaining = gallery.filter((img) => !img.selected).map((img) => img.url);
    setGallery(remaining.map((url) => ({ url, selected: false })));
    setContentData((prev) => ({ ...prev, images: remaining.join(',') }));
  };

  const handleSave = async () => {
    if (!contentData.title.trim()) {
      alert('Title is required.');
      return;
    }

    const contentHTML = editorRef.current?.innerHTML || '';
    const payload = {
      ...contentData,
      content: contentHTML,
      fontSize: contentData.fontSize,
    };

    try {
      if (isEditMode) {
        await ContentService.updateContent(id, payload);
        alert('Content updated successfully.');
        // If you want to go back to the view page after editing:
        // navigate(`/content/${id}`);
      } else {
        await ContentService.saveContent(payload);
        alert('Content saved successfully.');
      }
    } catch (err) {
      console.error(err);
      alert('Error saving content.');
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="content-editor-wrapper">
        <h2 className="text-2xl font-bold mb-6 text-center">
          {isEditMode ? 'Edit Content' : 'Content Creator'}
        </h2>

        {/* Title */}
        <div className="mb-4">
          <label className="block font-semibold mb-1">Title</label>
          <input
            className="w-full border p-2 rounded"
            value={contentData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
          />
        </div>

        {/* Category */}
        <div className="mb-4">
          <label className="block font-semibold mb-1">Category</label>
          <input
            className="w-full border p-2 rounded"
            value={contentData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
          />
        </div>

        {/* Extra Data */}
        <div className="mb-4">
          <label className="block font-semibold mb-1">Extra Data</label>
          <input
            className="w-full border p-2 rounded"
            value={contentData.extraData}
            onChange={(e) => handleInputChange('extraData', e.target.value)}
          />
        </div>

        {/* Font Size */}
        <div className="mb-4">
          <label className="block font-semibold mb-1">Font Size (px)</label>
          <input
            type="number"
            className="w-24 border p-2 rounded"
            value={contentData.fontSize}
            onChange={(e) => handleInputChange('fontSize', e.target.value)}
          />
        </div>

        {/* Markdown Preview Toggle */}
        <div className="mb-4">
          <label className="flex items-center gap-2 font-semibold">
            <input
              type="checkbox"
              checked={showPreview}
              onChange={() => setShowPreview(!showPreview)}
            />
            Show Markdown Preview
          </label>
        </div>

        {/* Formatting Toolbar */}
        <div className="content-toolbar flex flex-wrap gap-2 mb-4">
          <button onClick={() => applyFormat('bold')}>Bold</button>
          <button onClick={() => applyFormat('italic')}>Italic</button>
          <button onClick={() => applyFormat('underline')}>Underline</button>
          <button onClick={() => applyHeading(1)}>H1</button>
          <button onClick={() => applyHeading(2)}>H2</button>
          <button onClick={() => applyHeading(3)}>H3</button>
        </div>

        {/* Editor or Preview */}
        <div className="mb-6">
          <label className="block font-semibold mb-2">Content</label>
          <div className={showPreview ? 'content-preview-box' : 'content-editor-box'}>
            {showPreview ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: marked.parse(editorRef.current?.innerText || ''),
                }}
              />
            ) : (
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                style={{ fontSize: `${contentData.fontSize}px` }}
                dangerouslySetInnerHTML={{
                  __html:
                    contentData.content ||
                    '<p style="color:#888">Type your content here...</p>',
                }}
              ></div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-4">
          <button
            onClick={handleSave}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            {isEditMode ? 'Update' : 'Save'}
          </button>
          <button
            onClick={handleDeleteImages}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Delete Selected Images
          </button>
        </div>

        {/* Drag and Drop Upload */}
        <div
          onDrop={handleImageDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-dashed border-2 border-gray-400 rounded p-4 text-center text-sm text-gray-600 mb-2"
        >
          Drag and drop images here
        </div>

        {/* File Upload Button */}
        <div className="text-center mb-6">
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
          />
          <button
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Upload Image
          </button>
        </div>

        {/* Image Gallery */}
        <div className="image-grid">
          {gallery.map((img, idx) => (
            <div key={idx} className="image-grid-item">
              <img src={img.url} alt="" />
              <input
                type="checkbox"
                checked={img.selected}
                onChange={() => handleImageSelection(img.url)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContentHandler;
